"use client"

import React from 'react'
import { UIButton } from '../buttons/ui-button'
import { UITooltip } from '../overlay/ui-tooltip'
import { UITypography } from '../display/ui-typography'
import { Moon, Sun, Settings } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useDarkMode } from '../hooks/use-dark-mode'
import { UIBox } from './ui-box'

export interface UIFloatingPanelProps {
  className?: string
  children?: React.ReactNode
  showDarkModeToggle?: boolean
  darkModeTooltip?: string
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'
  triggerIcon?: React.ReactNode
  triggerTooltip?: string
}

const positionClasses = {
  'top-right': 'top-4 right-4',
  'top-left': 'top-4 left-4',
  'bottom-right': 'bottom-4 right-4',
  'bottom-left': 'bottom-4 left-4'
}

/**
 * 플로팅 패널 컴포넌트
 * 확장 가능한 설정 패널로 다양한 컨트롤을 포함할 수 있습니다.
 */
export const UIFloatingPanel = React.forwardRef<
  HTMLDivElement,
  UIFloatingPanelProps
>(({
  className,
  children,
  showDarkModeToggle = true,
  darkModeTooltip = "다크 모드 전환",
  position = 'top-right',
  triggerIcon,
  triggerTooltip = "설정 패널"
}, ref) => {
  const [isExpanded, setIsExpanded] = React.useState(false)
  const { toggle } = useDarkMode()

  const handleDarkModeToggle = () => {
    toggle()
  }

  return (
    <UIBox
      ref={ref}
      className={cn("fixed z-50", positionClasses[position], className)}
    >
      <UIBox className="flex flex-col gap-2 items-end">
        {/* Expanded panel content */}
        {isExpanded && (
          <UIBox className="bg-background/90 border-2 rounded-xl p-3 shadow-xl animate-in slide-in-from-top-2 duration-200">
            <UIBox className="flex flex-col gap-2">
              {showDarkModeToggle && (
                <UITooltip content={darkModeTooltip} side="left">
                  <UIButton
                    variant="ghost"
                    size="icon"
                    className="w-10 h-10"
                    onClick={handleDarkModeToggle}
                  >
                    <Sun className="h-[1.1rem] w-[1.1rem] rotate-0 scale-100 transition-transform dark:-rotate-90 dark:scale-0" />
                    <Moon className="absolute h-[1.1rem] w-[1.1rem] rotate-90 scale-0 transition-transform dark:rotate-0 dark:scale-100" />
                    <UITypography as="span" variant="small" className="sr-only">{darkModeTooltip}</UITypography>
                  </UIButton>
                </UITooltip>
              )}
              {children}
            </UIBox>
          </UIBox>
        )}

        {/* Main toggle button */}
        <UITooltip content={triggerTooltip} side="left">
          <UIButton
            variant="outline"
            size="icon"
            className="rounded-full w-12 h-12 shadow-lg hover:shadow-xl transition-shadow bg-background/80 border-2"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {triggerIcon || (
              <Settings
                className={cn(
                  "h-[1.2rem] w-[1.2rem] transition-transform duration-200",
                  isExpanded && "rotate-90"
                )}
              />
            )}
            <UITypography as="span" variant="small" className="sr-only">{triggerTooltip}</UITypography>
          </UIButton>
        </UITooltip>
      </UIBox>
    </UIBox>
  )
})

UIFloatingPanel.displayName = "UIFloatingPanel"